import { api } from "./api";

function tsBase(orgId: string): string {
  return `/org/${orgId}/timesheet`;
}
function exBase(orgId: string): string {
  return `/org/${orgId}/expense`;
}

export interface TimesheetEntry {
  id: string;
  user_id: string;
  user_name?: string;
  work_date: string;
  hours: string;
  notes?: string | null;
  project_code?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  billable?: boolean | null;
  billing_category?: string | null;
  client?: string | null;
  status: string;
  submission_id?: string | null;
  source?: string;
  decision_comment?: string | null;
}

export interface TimesheetSubmission {
  id: string;
  user_id: string;
  user_name?: string;
  period_start: string;
  period_end: string;
  status: string;
  entry_count: number;
  hours: string;
  decision_comment?: string | null;
}

export interface TimesheetProject {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

export interface TimesheetFavorite {
  id: string;
  name: string;
  description?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  billable?: boolean | null;
  billing_category?: string | null;
}

export interface TimesheetTimer {
  id: string;
  description: string;
  project_id?: string | null;
  project_name?: string | null;
  status: "running" | "paused";
  elapsed_seconds: number;
}

export interface ExpenseLine {
  id?: string;
  spent_on: string;
  category: string;
  amount: string | number;
  notes?: string | null;
  receipt_key?: string | null;
  receipt_filename?: string | null;
  receipt_content_type?: string | null;
  receipt_size?: number | null;
  vendor?: string | null;
  tax_amount?: string | number | null;
  currency?: string | null;
  expense_type?: string | null;
  project_code?: string | null;
  cost_centre?: string | null;
  client_code?: string | null;
  employee_paid?: boolean;
  missing_receipt?: boolean;
  comments?: string | null;
  destination?: string | null;
  mileage_km?: string | number | null;
  mileage_rate?: string | number | null;
  per_diem_rate?: string | number | null;
  per_diem_days?: string | number | null;
}

export interface ExpenseReport {
  id: string;
  user_id: string;
  user_name?: string;
  title: string;
  description?: string | null;
  currency: string;
  status: string;
  total: string;
  reimbursable_total?: string;
  claim_number?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  lines: ExpenseLine[];
  decision_comment?: string | null;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  gl_code?: string | null;
  amount_limit?: string | null;
  receipt_required: boolean;
  active: boolean;
}

export interface ExpenseSettings {
  default_mileage_rate?: string | null;
}

export type TimesheetDraft = {
  work_date: string;
  hours: number;
  notes?: string;
  project_code?: string;
  project_id?: string;
  billable?: boolean;
  billing_category?: string;
  client?: string;
};

export const workforceApi = {
  listTimesheets: (orgId: string) => api.get<{ entries: TimesheetEntry[] }>(`${tsBase(orgId)}/entries`),
  createTimesheet: (orgId: string, body: TimesheetDraft) =>
    api.post<TimesheetEntry>(`${tsBase(orgId)}/entries`, body),
  saveTimesheetGrid: (orgId: string, rows: Array<Omit<TimesheetDraft, "work_date" | "hours"> & { days: Record<string, number> }>) =>
    api.post<{ entries: TimesheetEntry[] }>(`${tsBase(orgId)}/entries/grid`, { rows }),
  patchTimesheet: (orgId: string, id: string, body: Partial<TimesheetDraft>) =>
    api.patch<TimesheetEntry>(`${tsBase(orgId)}/entries/${id}`, body),
  deleteTimesheet: (orgId: string, id: string) => api.delete<{ deleted: boolean }>(`${tsBase(orgId)}/entries/${id}`),
  submitTimesheet: (orgId: string, id: string) => api.post<TimesheetEntry>(`${tsBase(orgId)}/entries/${id}/submit`, {}),
  approveTimesheet: (orgId: string, id: string, comment?: string) =>
    api.post<TimesheetEntry>(`${tsBase(orgId)}/entries/${id}/approve`, { comment }),
  rejectTimesheet: (orgId: string, id: string, comment?: string) =>
    api.post<TimesheetEntry>(`${tsBase(orgId)}/entries/${id}/reject`, { comment }),

  listSubmissions: (orgId: string) => api.get<{ submissions: TimesheetSubmission[] }>(`${tsBase(orgId)}/submissions`),
  submitPeriod: (orgId: string, period_start: string, period_end: string) =>
    api.post<TimesheetSubmission>(`${tsBase(orgId)}/submissions`, { period_start, period_end }),
  withdrawSubmission: (orgId: string, id: string) =>
    api.post<TimesheetSubmission>(`${tsBase(orgId)}/submissions/${id}/withdraw`, {}),
  deleteSubmission: (orgId: string, id: string) =>
    api.delete<{ deleted: boolean }>(`${tsBase(orgId)}/submissions/${id}`),
  resubmitSubmission: (orgId: string, id: string) =>
    api.post<TimesheetSubmission>(`${tsBase(orgId)}/submissions/${id}/resubmit`, {}),
  approveSubmission: (orgId: string, id: string, comment?: string) =>
    api.post<TimesheetSubmission>(`${tsBase(orgId)}/submissions/${id}/approve`, { comment }),
  rejectSubmission: (orgId: string, id: string, comment?: string) =>
    api.post<TimesheetSubmission>(`${tsBase(orgId)}/submissions/${id}/reject`, { comment }),

  listProjects: (orgId: string, includeInactive = false) =>
    api.get<{ projects: TimesheetProject[] }>(`${tsBase(orgId)}/projects?include_inactive=${includeInactive}`),
  createProject: (orgId: string, body: { code: string; name: string }) =>
    api.post<TimesheetProject>(`${tsBase(orgId)}/projects`, body),
  patchProject: (orgId: string, id: string, body: { code: string; name: string; active?: boolean }) =>
    api.patch<TimesheetProject>(`${tsBase(orgId)}/projects/${id}`, body),

  listFavorites: (orgId: string) => api.get<{ favorites: TimesheetFavorite[] }>(`${tsBase(orgId)}/favorites`),
  createFavorite: (orgId: string, body: FavoriteIn) => api.post<TimesheetFavorite>(`${tsBase(orgId)}/favorites`, body),
  deleteFavorite: (orgId: string, id: string) => api.delete<{ deleted: boolean }>(`${tsBase(orgId)}/favorites/${id}`),

  getTimer: (orgId: string) => api.get<{ timer: TimesheetTimer | null }>(`${tsBase(orgId)}/timer`),
  startTimer: (orgId: string, body: { description?: string; project_id?: string }) =>
    api.post<TimesheetTimer>(`${tsBase(orgId)}/timer/start`, body),
  pauseTimer: (orgId: string) => api.post<TimesheetTimer>(`${tsBase(orgId)}/timer/pause`, {}),
  resumeTimer: (orgId: string) => api.post<TimesheetTimer>(`${tsBase(orgId)}/timer/resume`, {}),
  cancelTimer: (orgId: string) => api.post<{ deleted: boolean }>(`${tsBase(orgId)}/timer/cancel`, {}),
  convertTimer: (orgId: string, work_date?: string) =>
    api.post<TimesheetEntry>(`${tsBase(orgId)}/timer/convert`, { work_date }),

  listExpenses: (orgId: string) => api.get<{ reports: ExpenseReport[] }>(`${exBase(orgId)}/reports`),
  getExpense: (orgId: string, id: string) => api.get<ExpenseReport>(`${exBase(orgId)}/reports/${id}`),
  createExpense: (orgId: string, body: { title: string; currency?: string; description?: string; period_start?: string; period_end?: string; lines?: ExpenseLine[] }) =>
    api.post<ExpenseReport>(`${exBase(orgId)}/reports`, body),
  patchExpense: (orgId: string, id: string, body: { title?: string; currency?: string; description?: string; period_start?: string; period_end?: string }) =>
    api.patch<ExpenseReport>(`${exBase(orgId)}/reports/${id}`, body),
  deleteExpense: (orgId: string, id: string) => api.delete<{ deleted: boolean }>(`${exBase(orgId)}/reports/${id}`),
  addExpenseLine: (orgId: string, id: string, body: ExpenseLine) =>
    api.post<ExpenseReport>(`${exBase(orgId)}/reports/${id}/lines`, body),
  addExpenseGrid: (orgId: string, id: string, lines: ExpenseLine[]) =>
    api.post<ExpenseReport>(`${exBase(orgId)}/reports/${id}/lines/grid`, { lines }),
  patchExpenseLine: (orgId: string, reportId: string, lineId: string, body: ExpenseLine) =>
    api.patch<ExpenseReport>(`${exBase(orgId)}/reports/${reportId}/lines/${lineId}`, body),
  uploadExpenseReceipt: (orgId: string, reportId: string, lineId: string, file: File) =>
    api.upload<ExpenseReport>(`${exBase(orgId)}/reports/${reportId}/lines/${lineId}/receipt`, file),
  getExpenseReceipt: (orgId: string, reportId: string, lineId: string) =>
    api.getLink(`${exBase(orgId)}/reports/${reportId}/lines/${lineId}/receipt`),
  deleteExpenseReceipt: (orgId: string, reportId: string, lineId: string) =>
    api.delete<ExpenseReport>(`${exBase(orgId)}/reports/${reportId}/lines/${lineId}/receipt`),
  deleteExpenseLine: (orgId: string, reportId: string, lineId: string) =>
    api.delete<ExpenseReport>(`${exBase(orgId)}/reports/${reportId}/lines/${lineId}`),
  withdrawExpense: (orgId: string, id: string) =>
    api.post<ExpenseReport>(`${exBase(orgId)}/reports/${id}/withdraw`, {}),
  submitExpense: (orgId: string, id: string) => api.post<ExpenseReport>(`${exBase(orgId)}/reports/${id}/submit`, {}),
  approveExpense: (orgId: string, id: string, comment?: string) =>
    api.post<ExpenseReport>(`${exBase(orgId)}/reports/${id}/approve`, { comment }),
  rejectExpense: (orgId: string, id: string, comment?: string) =>
    api.post<ExpenseReport>(`${exBase(orgId)}/reports/${id}/reject`, { comment }),

  listCategories: (orgId: string, includeInactive = false) =>
    api.get<{ categories: ExpenseCategory[] }>(`${exBase(orgId)}/categories?include_inactive=${includeInactive}`),
  createCategory: (orgId: string, body: { name: string; gl_code?: string; amount_limit?: number; receipt_required?: boolean }) =>
    api.post<ExpenseCategory>(`${exBase(orgId)}/categories`, body),
  patchCategory: (orgId: string, id: string, body: Partial<ExpenseCategory>) =>
    api.patch<ExpenseCategory>(`${exBase(orgId)}/categories/${id}`, body),
  seedCategories: (orgId: string) => api.post<{ created: number; categories: ExpenseCategory[] }>(`${exBase(orgId)}/categories/seed`, {}),
  getSettings: (orgId: string) => api.get<ExpenseSettings>(`${exBase(orgId)}/settings`),
  putSettings: (orgId: string, body: ExpenseSettings) => api.put<ExpenseSettings>(`${exBase(orgId)}/settings`, body),
};

type FavoriteIn = {
  name?: string;
  description?: string;
  project_id?: string;
  billable?: boolean;
  billing_category?: string;
};
